import React from 'react';
import {Box} from '@mui/material';
import {ACTES, ACTE_LIBELLES, VERDICTS, VERDICT_LIBELLES} from '../../tokens.js';
import ChipFiltre from '../ChipFiltre.js';
import CircuitsSelect from '../CircuitsSelect.js';
import {BoutonPilule, CHAMP_PILULE, IconeLien, IconeTelecharger, LIBELLE_CHAMP} from './CarteUi.js';

const SEPARATEUR = <Box component="span" aria-hidden="true" sx={{width: '1px', height: 24, backgroundColor: '#DDE2E8'}}/>;

/**
 * R1 : barre de filtres sur deux lignes. Elle ne calcule rien : les groupes, comptes et le résumé viennent de la page.
 * `inerte` (chargement) : opacité .55 et aucune interaction.
 */
export default function CarteBarreFiltres({
    vue, index, groupes, comptesVerdict, inerte, exportsActifs, nbFiltresActifs, onChange, onReinitialiser, onCopier, onCsv, onGeoJson,
}) {
    const choisis = new Set(vue.patrimoines);
    const basculerGroupe = (g) => {
        const tous = g.ids.every((id) => choisis.has(id));
        const suivant = new Set(choisis);
        g.ids.forEach((id) => (tous ? suivant.delete(id) : suivant.add(id)));
        onChange({patrimoines: [...suivant].sort((a, b) => a - b)}, {push: true});
    };
    const basculerEtat = (v) => {
        const suivant = vue.etats.includes(v) ? vue.etats.filter((e) => e !== v) : [...vue.etats, v];
        onChange({etats: VERDICTS.filter((e) => suivant.includes(e))}, {push: true});
    };

    return (
        <Box
            aria-busy={inerte ? 'true' : undefined}
            sx={{
                display: 'flex', flexDirection: 'column', gap: '10px', px: '24px', py: '12px', backgroundColor: '#FFFFFF',
                borderBottom: '1px solid #E3E7EC', flex: '0 0 auto',
                ...(inerte ? {opacity: 0.55, pointerEvents: 'none'} : {}),
            }}
        >
            {/* Ligne 1 : patrimoines · acte · permalien et exports */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap'}}>
                <Box role="group" aria-label="Patrimoines" sx={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                    <Box component="span" sx={{fontSize: 12, color: '#6B7480', mr: '4px'}}>Patrimoines</Box>
                    {groupes.map((g) => (
                        <ChipFiltre
                            key={g.id}
                            label={g.libelle}
                            count={g.nb}
                            checked={g.ids.length > 0 && g.ids.every((id) => choisis.has(id))}
                            indeterminate={g.ids.some((id) => choisis.has(id)) && !g.ids.every((id) => choisis.has(id))}
                            onChange={() => basculerGroupe(g)}
                        />
                    ))}
                    <CircuitsSelect index={index} valeur={vue.patrimoines} onChange={(ids) => onChange({patrimoines: ids}, {push: true})} inerte={!index}/>
                </Box>
                {SEPARATEUR}
                <label style={LIBELLE_CHAMP}>
                    Acte
                    <select value={vue.acte} onChange={(e) => onChange({acte: e.target.value}, {push: true})} style={CHAMP_PILULE}>
                        {ACTES.map((a) => (
                            <option key={a} value={a} disabled={a === 'collecte' && !vue.collecte}>
                                {a === 'collecte' && !vue.collecte ? 'Collecte — non affiché' : ACTE_LIBELLES[a]}
                            </option>
                        ))}
                    </select>
                </label>
                <Box sx={{flex: '1 1 40px'}}/>
                <BoutonPilule onClick={onCopier} icone={IconeLien}>Copier le permalien</BoutonPilule>
                <BoutonPilule onClick={onCsv} disabled={!exportsActifs} icone={IconeTelecharger}>CSV</BoutonPilule>
                <BoutonPilule onClick={onGeoJson} disabled={!exportsActifs} icone={IconeTelecharger}>GeoJSON</BoutonPilule>
            </Box>

            {/* Ligne 2 : période · commune · états (légende) · élément en défaut */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <label style={{...LIBELLE_CHAMP, gap: 6}}>
                        Du
                        <input type="date" value={vue.du} max={vue.au || undefined} onChange={(e) => onChange({du: e.target.value}, {push: true})} style={{...CHAMP_PILULE, width: 138}}/>
                    </label>
                    <label style={{...LIBELLE_CHAMP, gap: 6}}>
                        Au
                        <input type="date" value={vue.au} min={vue.du || undefined} onChange={(e) => onChange({au: e.target.value}, {push: true})} style={{...CHAMP_PILULE, width: 138}}/>
                    </label>
                </Box>
                {SEPARATEUR}
                <label style={LIBELLE_CHAMP}>
                    Commune
                    <select value={vue.commune} onChange={(e) => onChange({commune: e.target.value}, {push: true})} style={CHAMP_PILULE}>
                        <option value="">Toutes</option>
                        {(index ? index.communes : []).map((c) => <option key={c.cle} value={c.cle}>{c.libelle}</option>)}
                    </select>
                </label>
                <Box role="group" aria-label="État" sx={{display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                    <Box component="span" sx={{fontSize: 12, color: '#6B7480'}}>État</Box>
                    {VERDICTS.map((v) => (
                        <ChipFiltre
                            key={v}
                            verdict={v}
                            label={VERDICT_LIBELLES[v]}
                            count={comptesVerdict ? comptesVerdict[v] : null}
                            checked={vue.etats.includes(v)}
                            onChange={() => basculerEtat(v)}
                        />
                    ))}
                </Box>
                <label style={LIBELLE_CHAMP}>
                    Élément en défaut
                    <select value={vue.element} onChange={(e) => onChange({element: e.target.value}, {push: true})} style={CHAMP_PILULE}>
                        <option value="">Tous</option>
                        {(index ? [...index.elements].sort((a, b) => a.localeCompare(b)) : []).map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                </label>
                {nbFiltresActifs > 0 && (
                    <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 12, px: '10px', py: '4px', border: '1px solid #E3E7EC', borderRadius: '999px', backgroundColor: '#F6F8F4'}}>
                        {nbFiltresActifs} {nbFiltresActifs > 1 ? 'filtres actifs' : 'filtre actif'}
                        <Box component="button" type="button" onClick={onReinitialiser} sx={{border: 0, background: 'none', p: 0, font: 'inherit', fontWeight: 600, color: '#213067', cursor: 'pointer', textDecoration: 'underline'}}>
                            Tout réinitialiser
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
